import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import NotebookView from '../NotebookView';
import StatusBar from '../StatusBar';
import NotebookSidebar from '../NotebookSidebar';
import AgentSidebar from './AgentSidebar';
import ProjectOverview from './ProjectOverview';
import ExperimentTracker from './ExperimentTracker';
import DatasetBrowser from './DatasetBrowser';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { API_BASE_URL, WS_BASE } from '../config';
import Settings from './Settings';

const API_BASE = API_BASE_URL;
const ADMIN_EMAILS = ["team@scifi.ink"];

function DSWorkspace() {
    const { user, token, logout } = useAuth();
    const [notebooks, setNotebooks] = useState([]);
    const [currentNotebook, setCurrentNotebook] = useState(null);
    const [notebookData, setNotebookData] = useState(null);
    const [chatHistory, setChatHistory] = useState([]);
    const [status, setStatus] = useState({ message: 'Ready', type: 'idle' });
    const [showLeftSidebar, setShowLeftSidebar] = useState(true);
    const [showRightSidebar, setShowRightSidebar] = useState(true);
    const [isLoadingNotebook, setIsLoadingNotebook] = useState(false);

    // WebSocket state
    const wsRef = useRef(null);
    const [sessionId, setSessionId] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [executingCells, setExecutingCells] = useState(new Set());
    const [progressInfo, setProgressInfo] = useState(null);

    // Settings modal
    const [showSettingsModal, setShowSettingsModal] = useState(false);

    // DS OS specific state
    const [currentView, setCurrentView] = useState('notebook'); // 'notebook', 'overview', 'experiments', 'datasets'
    const [currentStage, setCurrentStage] = useState('data'); // 'data', 'engineer', 'train', 'eval', 'deploy'
    const [activeAgent, setActiveAgent] = useState('scout'); // Which agent is active
    const [experiments, setExperiments] = useState([]);
    const [pipelineStatus, setPipelineStatus] = useState({
        data: { status: 'active', agent: 'scout', progress: 100 },
        engineer: { status: 'complete', agent: 'engineer', progress: 100 },
        train: { status: 'active', agent: 'ml_engineer', progress: 67 },
        eval: { status: 'pending', agent: 'evaluator', progress: 0 },
        deploy: { status: 'pending', agent: 'mlops', progress: 0 }
    });

    // Auto-scroll state
    const notebookScrollRef = useRef(null);

    useEffect(() => {
        loadNotebooks();
    }, []);

    useEffect(() => {
        if (currentNotebook) {
            loadNotebook(currentNotebook);
            loadChatHistory(currentNotebook);
            connectWebSocket(currentNotebook);
            loadExperiments(currentNotebook);

            return () => {
                disconnectWebSocket();
            };
        }
    }, [currentNotebook]);

    const connectWebSocket = useCallback((notebookId) => {
        if (wsRef.current) {
            wsRef.current.close();
        }

        const ws = new WebSocket(`${WS_BASE}/ws/chat/${notebookId}${token ? `?token=${token}` : ''}`);

        ws.onopen = () => {
            console.log('WebSocket connected');
        };

        ws.onmessage = (event) => {
            const data = JSON.parse(event.data);
            handleWebSocketMessage(data);
        };

        ws.onclose = () => {
            console.log('WebSocket disconnected');
            setSessionId(null);
        };

        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
            setStatus({ message: 'Connection error', type: 'error' });
        };

        wsRef.current = ws;
    }, [token]);

    const disconnectWebSocket = useCallback(() => {
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
        setSessionId(null);
    }, []);

    const handleWebSocketMessage = useCallback((data) => {
        switch (data.type) {
            case 'connected':
                setSessionId(data.session_id);
                break;

            case 'typing':
                setIsTyping(data.is_typing);
                break;

            case 'llm_response':
                setChatHistory(prev => [...prev, {
                    role: 'assistant',
                    content: data.text,
                    metadata: data.metadata,
                    agent: activeAgent
                }]);
                break;

            case 'cell_updated':
                setNotebookData(prev => {
                    if (!prev) return prev;
                    const cellExists = prev.cells.some(cell => cell.cell_id === data.cell.cell_id);
                    if (cellExists) {
                        return {
                            ...prev,
                            cells: prev.cells.map(cell =>
                                cell.cell_id === data.cell.cell_id ? data.cell : cell
                            )
                        };
                    } else {
                        return {
                            ...prev,
                            cells: [...prev.cells, data.cell]
                        };
                    }
                });
                break;

            case 'complete':
                setIsProcessing(false);
                setIsTyping(false);
                setStatus({ message: data.message, type: 'success' });
                break;

            case 'error':
                setIsProcessing(false);
                setIsTyping(false);
                setStatus({ message: data.message, type: 'error' });
                break;

            default:
                console.log('Unknown message type:', data.type);
        }
    }, [activeAgent]);

    const loadNotebooks = async () => {
        try {
            const response = await axios.get(`${API_BASE}/api/notebooks`);
            setNotebooks(response.data);
        } catch (error) {
            console.error('Error loading notebooks:', error);
        }
    };

    const loadNotebook = async (notebookId) => {
        try {
            setIsLoadingNotebook(true);
            setStatus({ message: 'Loading notebook...', type: 'loading' });
            const response = await axios.get(`${API_BASE}/api/notebooks/${notebookId}`);
            setNotebookData(response.data);
            setStatus({ message: 'Ready', type: 'idle' });
        } catch (error) {
            console.error('Error loading notebook:', error);
            setStatus({ message: 'Error loading notebook', type: 'error' });
        } finally {
            setIsLoadingNotebook(false);
        }
    };

    const loadChatHistory = async (notebookId) => {
        try {
            const response = await axios.get(`${API_BASE}/api/notebooks/${notebookId}/chat`);
            setChatHistory(response.data);
        } catch (error) {
            console.error('Error loading chat history:', error);
        }
    };

    const loadExperiments = async (notebookId) => {
        // Mock experiments for now - will integrate with backend
        setExperiments([
            {
                id: 'exp_001',
                name: 'exp_001',
                model: 'logistic',
                params: { C: 1.0 },
                metrics: { accuracy: 0.87, f1: 0.86 },
                status: 'complete',
                duration: '5m',
                created_at: new Date()
            },
            {
                id: 'exp_002',
                name: 'exp_002',
                model: 'bert-base',
                params: { lr: 2e-5, batch_size: 16, epochs: 3 },
                metrics: { accuracy: 0.94, f1: 0.93 },
                status: 'complete',
                duration: '4h',
                created_at: new Date(),
                is_best: true
            },
            {
                id: 'exp_003',
                name: 'exp_003',
                model: 'distilbert',
                params: { lr: 3e-5, batch_size: 16, epochs: 3 },
                metrics: { accuracy: 0.91, f1: 0.90 },
                status: 'running',
                duration: '1h',
                created_at: new Date()
            }
        ]);
    };

    const createNotebook = async (title = 'Untitled Notebook') => {
        try {
            const response = await axios.post(`${API_BASE}/api/notebooks`, { title });
            await loadNotebooks();
            setCurrentNotebook(response.data.id);
            return response.data.id;
        } catch (error) {
            console.error('Error creating notebook:', error);
            return null;
        }
    };

    const deleteNotebook = async (notebookId) => {
        try {
            await axios.delete(`${API_BASE}/api/notebooks/${notebookId}`);
            await loadNotebooks();
            if (currentNotebook === notebookId) {
                setCurrentNotebook(null);
                setNotebookData(null);
            }
        } catch (error) {
            console.error('Error deleting notebook:', error);
        }
    };

    const renameNotebook = async (notebookId, newTitle) => {
        try {
            await axios.put(`${API_BASE}/api/notebooks/${notebookId}`, { title: newTitle });
            await loadNotebooks();
            if (currentNotebook === notebookId) {
                setNotebookData(prev => ({ ...prev, title: newTitle }));
            }
        } catch (error) {
            console.error('Error renaming notebook:', error);
        }
    };

    const addCell = async (cellType, content, cellId = null) => {
        if (!currentNotebook) return;
        try {
            await axios.post(`${API_BASE}/api/notebooks/${currentNotebook}/cells`, {
                cell_type: cellType,
                content,
                cell_id: cellId
            });
            await loadNotebook(currentNotebook);
        } catch (error) {
            console.error('Error adding cell:', error);
        }
    };

    const updateCell = async (cellId, content) => {
        try {
            await axios.put(`${API_BASE}/api/cells/${cellId}`, { content });
            await loadNotebook(currentNotebook);
        } catch (error) {
            console.error('Error updating cell:', error);
        }
    };

    const deleteCell = async (cellId) => {
        try {
            await axios.delete(`${API_BASE}/api/cells/${cellId}`);
            await loadNotebook(currentNotebook);
        } catch (error) {
            console.error('Error deleting cell:', error);
        }
    };

    const executeCell = async (cellId) => {
        try {
            setStatus({ message: 'Executing cell...', type: 'loading' });
            await axios.post(`${API_BASE}/api/cells/${cellId}/execute`);
            await loadNotebook(currentNotebook);
            setStatus({ message: 'Cell executed', type: 'success' });
            setTimeout(() => setStatus({ message: 'Ready', type: 'idle' }), 2000);
        } catch (error) {
            console.error('Error executing cell:', error);
            setStatus({ message: 'Execution error', type: 'error' });
        }
    };

    const moveCell = async (cellId, direction) => {
        if (!currentNotebook) return;
        try {
            await axios.post(`${API_BASE}/api/notebooks/${currentNotebook}/cells/reorder`, {
                cell_id: cellId,
                direction: direction
            });
            await loadNotebook(currentNotebook);
        } catch (error) {
            console.error('Error moving cell:', error);
        }
    };

    const sendChatMessage = useCallback((message, agent = null) => {
        if (!currentNotebook || !wsRef.current) return;

        if (wsRef.current.readyState !== WebSocket.OPEN) {
            setStatus({ message: 'Not connected', type: 'error' });
            return;
        }

        const targetAgent = agent || activeAgent;

        setChatHistory(prev => [...prev, {
            role: 'user',
            content: message,
            agent: targetAgent
        }]);

        setIsProcessing(true);
        setStatus({ message: `${getAgentName(targetAgent)} is thinking...`, type: 'loading' });

        wsRef.current.send(JSON.stringify({
            type: 'chat',
            message: message,
            agent: targetAgent
        }));
    }, [currentNotebook, activeAgent]);

    const getAgentName = (agent) => {
        const names = {
            scout: 'Data Scout',
            engineer: 'Data Engineer',
            ml_engineer: 'ML Engineer',
            evaluator: 'Evaluator',
            mlops: 'MLOps Engineer',
            mentor: 'Mentor'
        };
        return names[agent] || 'AI';
    };

    const renderMainContent = () => {
        switch (currentView) {
            case 'overview':
                return (
                    <ProjectOverview
                        pipelineStatus={pipelineStatus}
                        currentStage={currentStage}
                        onStageChange={setCurrentStage}
                        experiments={experiments}
                    />
                );
            case 'experiments':
                return (
                    <ExperimentTracker
                        experiments={experiments}
                        onSelectExperiment={(exp) => console.log('Select experiment:', exp)}
                        onCompareExperiments={(exps) => console.log('Compare:', exps)}
                    />
                );
            case 'datasets':
                return (
                    <DatasetBrowser
                        onSelectDataset={(dataset) => console.log('Select dataset:', dataset)}
                    />
                );
            case 'notebook':
            default:
                return (
                    <div ref={notebookScrollRef} className="flex-1 overflow-auto">
                        {notebookData ? (
                            <NotebookView
                                notebook={notebookData}
                                onAddCell={addCell}
                                onUpdateCell={updateCell}
                                onDeleteCell={deleteCell}
                                onExecuteCell={executeCell}
                                onMoveCell={moveCell}
                                executingCells={executingCells}
                            />
                        ) : (
                            <div className="flex items-center justify-center h-full text-gray-500">
                                <div className="text-center">
                                    <p className="text-lg mb-4">No notebook selected</p>
                                    <button
                                        onClick={() => createNotebook()}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                    >
                                        Create New Project
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                );
        }
    };

    return (
        <div className="flex h-screen bg-gray-50">
            {/* Left Sidebar - File Explorer */}
            {showLeftSidebar && (
                <NotebookSidebar
                    notebooks={notebooks}
                    currentNotebook={currentNotebook}
                    onSelectNotebook={setCurrentNotebook}
                    onCreateNotebook={createNotebook}
                    onDeleteNotebook={deleteNotebook}
                    onRenameNotebook={renameNotebook}
                    onCloseSidebar={() => setShowLeftSidebar(false)}
                />
            )}

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col">
                {/* Top Header */}
                <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        {!showLeftSidebar && (
                            <button
                                onClick={() => setShowLeftSidebar(true)}
                                className="text-gray-600 hover:text-gray-900"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            </button>
                        )}
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">
                                sciFi DS OS
                            </h1>
                            <p className="text-xs text-gray-500">
                                {notebookData?.title || 'Data Science Operating System'}
                            </p>
                        </div>

                        {/* View Selector */}
                        <div className="flex gap-1 ml-4 bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => setCurrentView('overview')}
                                className={`px-3 py-1 text-xs font-medium rounded ${
                                    currentView === 'overview' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                🎯 Overview
                            </button>
                            <button
                                onClick={() => setCurrentView('notebook')}
                                className={`px-3 py-1 text-xs font-medium rounded ${
                                    currentView === 'notebook' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                📓 Notebook
                            </button>
                            <button
                                onClick={() => setCurrentView('experiments')}
                                className={`px-3 py-1 text-xs font-medium rounded ${
                                    currentView === 'experiments' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                🔬 Experiments
                            </button>
                            <button
                                onClick={() => setCurrentView('datasets')}
                                className={`px-3 py-1 text-xs font-medium rounded ${
                                    currentView === 'datasets' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                📦 Datasets
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <StatusBar status={status} progressInfo={progressInfo} />
                        <div className="flex items-center gap-2 ml-4 pl-4 border-l border-gray-200">
                            <button
                                onClick={() => setShowSettingsModal(true)}
                                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded border border-gray-200 transition-colors flex items-center gap-1"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Settings
                            </button>
                            <button
                                onClick={logout}
                                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded border border-gray-200 transition-colors flex items-center gap-1"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                                Logout
                            </button>
                        </div>
                    </div>
                </header>

                {/* Main Content + Right Sidebar */}
                <div className="flex-1 flex overflow-hidden relative">
                    {isLoadingNotebook && (
                        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
                            <div className="flex flex-col items-center">
                                <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
                                <p className="text-gray-700 font-medium">Loading notebook...</p>
                            </div>
                        </div>
                    )}
                    {renderMainContent()}

                    {/* Right Sidebar - AI Agents */}
                    {showRightSidebar && currentNotebook && (
                        <AgentSidebar
                            activeAgent={activeAgent}
                            onAgentChange={setActiveAgent}
                            chatHistory={chatHistory}
                            onSendMessage={sendChatMessage}
                            isProcessing={isProcessing}
                            isTyping={isTyping}
                            currentStage={currentStage}
                            pipelineStatus={pipelineStatus}
                            onCloseSidebar={() => setShowRightSidebar(false)}
                        />
                    )}
                </div>
            </div>

            {showSettingsModal && (
                <Settings
                    isOpen={showSettingsModal}
                    onClose={() => setShowSettingsModal(false)}
                />
            )}
        </div>
    );
}

export default DSWorkspace;
